import { Visibility, VisibilityOff } from '@mui/icons-material';
import LockIcon from '@mui/icons-material/Lock';
import {
  Box,
  Button,
  CircularProgress,
  IconButton,
  InputAdornment,
  Paper,
  TextField,
  Typography,
} from '@mui/material';
import { useEffect, useState } from 'react';
import { AppGateService } from '../../services/TempGatewayService';

const styles = {
  container: {
    minHeight: '100vh',
    width: '100%',
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    backgroundImage:
      'linear-gradient(rgba(0, 0, 0, 0.9), rgba(0, 0, 0, 0.9)), url("/viewer_octagon.png")',
    backgroundSize: 'cover',
    backgroundPosition: 'center',
    backgroundRepeat: 'no-repeat',
  },
  gateBox: {
    width: '100%',
    maxWidth: '400px',
    padding: '30px',
    borderRadius: '8px',
    backgroundColor: 'rgba(20, 20, 20, 0.9)',
    border: '1px solid rgba(255, 255, 255, 0.1)',
    boxShadow: '0 4px 20px rgba(0, 0, 0, 0.7)',
    textAlign: 'center',
  },
  logo: {
    maxWidth: '150px',
    marginBottom: '20px',
    filter: 'drop-shadow(0 2px 5px rgba(0, 0, 0, 0.5))',
  },
  title: {
    fontFamily: 'Rubik Mono One',
    color: '#ffffff',
    fontSize: 'clamp(1.3rem, 2vw, 1.8rem)',
    marginBottom: '20px',
    textTransform: 'uppercase',
  },
  subtitle: {
    color: 'rgba(255, 255, 255, 0.7)',
    marginBottom: '30px',
    fontSize: '0.9rem',
  },
  input: {
    marginBottom: '20px',
    backgroundColor: 'rgba(0, 0, 0, 0.4)',
    borderRadius: '4px',
    '& .MuiOutlinedInput-root': {
      '& fieldset': {
        borderColor: 'rgba(255, 255, 255, 0.2)',
      },
      '&:hover fieldset': {
        borderColor: 'rgba(255, 255, 255, 0.3)',
      },
    },
  },
  submitButton: {
    backgroundColor: '#8B0000',
    color: 'white',
    fontWeight: 'bold',
    padding: '10px 0',
    width: '100%',
    marginTop: '10px',
    '&:hover': {
      backgroundColor: '#A00000',
    },
    '&:disabled': {
      backgroundColor: 'rgba(139, 0, 0, 0.5)',
      color: 'rgba(255, 255, 255, 0.5)',
    },
  },
  errorMessage: {
    color: '#ff6b6b',
    marginBottom: '15px',
    fontSize: '0.9rem',
  },
};

export const GatePage = () => {
  const [password, setPassword] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');
  const [showPassword, setShowPassword] = useState(false);

  useEffect(() => {
    // Check if user is already authorized
    if (AppGateService.isAuthorized()) {
      window.location.href = '/home';
    }
  }, []);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!password) {
      setError('Password is required');
      return;
    }

    setIsLoading(true);
    setError('');

    try {
      const success = await AppGateService.verifyAppPassword(password);
      if (success) {
        window.location.href = '/home';
      } else {
        setError('Invalid password. Please try again.');
      }
    } catch (err) {
      setError('An error occurred. Please try again.');
      console.error('Gate error:', err);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <Box sx={styles.container}>
      <Paper elevation={0} sx={styles.gateBox}>
        <img src="/fantasy_mma_logo.png" alt="Logo" style={styles.logo} />

        <Typography variant="h5" sx={styles.title}>
          Private Access
        </Typography>

        <Typography variant="body2" sx={styles.subtitle}>
          This application is currently in private beta. Please enter the access
          password to continue.
        </Typography>

        {error && (
          <Typography variant="body2" sx={styles.errorMessage}>
            {error}
          </Typography>
        )}

        <form onSubmit={handleSubmit}>
          <TextField
            label="Access Password"
            variant="outlined"
            type={showPassword ? 'text' : 'password'}
            fullWidth
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            sx={styles.input}
            autoFocus
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <LockIcon sx={{ color: '#cc0000' }} />
                </InputAdornment>
              ),
              endAdornment: (
                <InputAdornment position="end">
                  <IconButton
                    onClick={() => setShowPassword(!showPassword)}
                    edge="end"
                  >
                    {showPassword ? <VisibilityOff /> : <Visibility />}
                  </IconButton>
                </InputAdornment>
              ),
            }}
            onKeyPress={(e) => {
              if (e.key === 'Enter') {
                handleSubmit(e);
              }
            }}
          />

          <Button
            type="submit"
            variant="contained"
            sx={styles.submitButton}
            disabled={isLoading || !password}
          >
            {isLoading ? (
              <CircularProgress size={24} color="inherit" />
            ) : (
              'Enter Application'
            )}
          </Button>
        </form>
      </Paper>
    </Box>
  );
};

export default GatePage;
